import { RateLimit } from 'async-sema';
import ticketStore from '../bets/ticket-store';
import { Ticket, enrichScannedTicket, TicketView, getCurrentDateTime } from '../model';
import { hasDisabledVenue } from './validation';
import { partition } from 'lodash';
import { AmericanWageringGateway } from './american-wagering-gateway';
import { DynamicConfig } from './dynamic-config';

const maxConcurrentRequests = 5;

export async function queryAndAddTicket({
  retailTicketId,
  americanWageringGateway,
  abortController,
  dynamicConfig
}: {
  retailTicketId: string;
  americanWageringGateway: AmericanWageringGateway;
  dynamicConfig: DynamicConfig;
  abortController: AbortController;
}): Promise<TicketView> {
  const data = await americanWageringGateway.getTicketDetails({
    retailTicketId,
    abortController
  });

  const scannedAt = getCurrentDateTime();
  const ticket = enrichScannedTicket(data, scannedAt);
  ensureVenueEnabled(ticket, dynamicConfig);

  ticketStore.add(ticket);
  return ticket;
}

export async function queryAndUpdateTickets({
  retailTicketIds,
  abortController,
  americanWageringGateway,
  dynamicConfig
}: {
  retailTicketIds: string[];
  abortController: AbortController;
  dynamicConfig: DynamicConfig;
  americanWageringGateway: AmericanWageringGateway;
}) {
  const rateLimit = RateLimit(maxConcurrentRequests);

  const requests = retailTicketIds.map(async (retailTicketId: string) => {
    await rateLimit();
    try {
      return await americanWageringGateway.getTicketDetails({ retailTicketId, abortController });
    } catch (e) {
      throw new Error(e.message);
    }
  });

  const responses = await Promise.allSettled(requests);

  const fulfilled = responses
    .filter(r => r.status === 'fulfilled')
    .map(f => (f as PromiseFulfilledResult<Ticket>).value);

  const [ticketWithDisabledVenues, ticketsNotFromDisabledVenues] = partition(fulfilled, t =>
    hasDisabledVenue(t, dynamicConfig)
  );

  const rejected = responses.filter(r => r.status === 'rejected');
  const errors = await Promise.all(rejected.map(r => (r as PromiseRejectedResult).reason));

  ticketStore.update(ticketsNotFromDisabledVenues);

  if (errors.length > 0 || ticketWithDisabledVenues.length > 0) {
    const message = [
      ...errors,
      ...ticketWithDisabledVenues.map(t => `Tracking bet of the venue '${t.placedAtLocation.venueId}' is disabled.`)
    ].join('\n');
    throw new Error(message);
  }
}

function ensureVenueEnabled(ticket: TicketView, dynamicConfig: DynamicConfig) {
  if (hasDisabledVenue(ticket, dynamicConfig)) {
    throw new Error(`Bets from this venue cannot be tracked at this time`);
  }
}
